import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useInfoBanner } from 'app/components/InfoBannerProvider';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';
import { AuthorizedAccountsApi } from 'app/modules/settings/api/authorizedAccounts';
import { AuthorizationPage } from 'app/modules/settings/types/authorizedAccount';
import { queryKey } from 'constants/queryKey';
import { useUserPermissions } from 'app/hooks/useUserPermissions';
import { UserPermission } from 'constants/userPermission';
import { useUser } from 'modules/user/userSelector';
import { usePostsSelectionState } from 'app/modules/posts/hooks/usePostsSelectionState';
import { useSocialLoginContext } from 'app/modules/socialLogin/context';
import PageTypeUtils from 'app/utils/pageType';
import { addQueryParams } from 'utils/url';
import routes, { modalRoutes } from 'constants/routes';
import { BrowserStorage } from 'utils/browserStorage';
import Logger from 'utils/logger';
import CalendarDisconnectedProfilesBannerContent from 'components/calendarDisconnectedProfilesBanner/CalendarDisconnectedProfilesBannerContent';

function readDismissedPageIds(): number[] {
  try {
    const stored = sessionStorage.getItem(
      BrowserStorage.keys.Banners.CalendarDisconnectedProfilesDismissed,
    );
    const parsed = stored ? JSON.parse(stored) : [];

    return Array.isArray(parsed)
      ? parsed.filter((id): id is number => typeof id === 'number')
      : [];
  } catch {
    return [];
  }
}

function writeDismissedPageIds(pageIds: number[]): number[] {
  const sorted = Array.from(new Set(pageIds)).sort((a, b) => a - b);

  try {
    sessionStorage.setItem(
      BrowserStorage.keys.Banners.CalendarDisconnectedProfilesDismissed,
      JSON.stringify(sorted),
    );
  } catch (e) {
    Logger.error(e);
  }

  return sorted;
}

function parseIdsFromQueryParam(value: string | null): number[] {
  return (value ?? '')
    .split(',')
    .map((id) => Number.parseInt(id, 10))
    .filter((id) => Number.isFinite(id));
}

/**
 * Warns about disconnected profiles the user currently has selected in the calendar,
 * with a reconnect CTA and the number of scheduled posts at risk per profile.
 *
 * Lives in the app-wide InfoBanner slot but is scoped to the calendar, so it both
 * shows only there and clears itself when the user navigates away.
 */
export const useCalendarDisconnectedProfilesBanner = () => {
  const { showBanner, clearBanner } = useInfoBanner();
  const { pathname, search } = useLocation();
  const { hasSome } = useUserPermissions();
  const { isClient } = useUser();
  const selectedProfiles = usePostsSelectionState(
    (state) => state.selectedProfiles,
  );
  const selectedGroups = usePostsSelectionState(
    (state) => state.selectedGroups,
  );
  const profileGroups = usePostsSelectionState((state) => state.profileGroups);
  const socialLogin = useSocialLoginContext();
  const socialLoginRef = useRef(socialLogin);
  socialLoginRef.current = socialLogin;
  const [dismissedPageIds, setDismissedPageIds] =
    useState<number[]>(readDismissedPageIds);
  const ownsBannerRef = useRef(false);

  // useLocation() strips the router basename, so this compares against '/posts/calendar'
  // rather than '/app/posts/calendar'. Adding the prefix here would break the match.
  const isCalendar = pathname.startsWith(routes.POSTS_CALENDAR);

  const canAccessSocialLogin =
    !isClient && hasSome(UserPermission.ACCESS_SOCIAL_LOGIN);

  const selectedPageIds = useMemo(() => {
    if (!isCalendar) {
      return [];
    }

    // The posts filter state is the source of truth, published to the store by the posts
    // Container. Until it mounts the store is still empty, so fall back to the url — which
    // only carries the selection once a filter has changed, never on a calendar restored
    // from the user's saved selection.
    const params = new URLSearchParams(search);
    const hasPublishedSelection =
      selectedProfiles.length > 0 || selectedGroups.length > 0;
    const profileIds = hasPublishedSelection
      ? selectedProfiles
      : parseIdsFromQueryParam(params.get('profiles'));
    const groupIds = hasPublishedSelection
      ? selectedGroups
      : parseIdsFromQueryParam(params.get('groups'));
    const groupPageIds = profileGroups
      .filter((group) => groupIds.includes(group.id))
      .flatMap((group) => group.pages.map((page) => page.id));

    return Array.from(new Set([...profileIds, ...groupPageIds]));
  }, [isCalendar, search, selectedProfiles, selectedGroups, profileGroups]);

  const disconnectedPagesQuery = useQuery(
    queryKey.authorizationPages('disconnected'),
    () => AuthorizedAccountsApi.getPages('disconnected'),
    {
      enabled: isCalendar && canAccessSocialLogin && selectedPageIds.length > 0,
    },
  );

  const disconnectedSelectedPages = useMemo(
    () =>
      (disconnectedPagesQuery.data ?? []).filter((page) =>
        selectedPageIds.includes(page.pageId),
      ),
    [disconnectedPagesQuery.data, selectedPageIds],
  );

  // A dismissal silences one incident, not the profile for the session. A profile that has
  // left the disconnected list has been reconnected, so the next time it breaks it counts as
  // a new incident and the banner is allowed back.
  //
  // Keyed on the full fetched list rather than the selected subset: deselecting a profile in
  // the calendar filter must not be mistaken for a reconnect.
  const disconnectedPageIdsSignature = (disconnectedPagesQuery.data ?? [])
    .map((page) => page.pageId)
    .sort((a, b) => a - b)
    .join(',');

  useEffect(() => {
    if (!disconnectedPagesQuery.isSuccess) {
      return;
    }

    const stillDisconnected = new Set(
      (disconnectedPagesQuery.data ?? []).map((page) => page.pageId),
    );

    setDismissedPageIds((current) => {
      const reconnected = current.filter((id) => !stillDisconnected.has(id));

      if (reconnected.length === 0) {
        return current;
      }

      return writeDismissedPageIds(
        current.filter((id) => stillDisconnected.has(id)),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disconnectedPagesQuery.isSuccess, disconnectedPageIdsSignature]);

  const handleReconnect = useCallback((page: AuthorizationPage) => {
    const pageType = PageTypeUtils.getNumberFromName(page.type);

    socialLoginRef.current.authorize({
      pageId: page.pageId,
      pageType,
      redirectUrl: `${addQueryParams(window.location.href, {
        id: page.pageId,
        type: pageType,
      })}#${modalRoutes.ASSOCIATE_POSTS_PROFILE}`,
    });
  }, []);

  const isDismissed =
    disconnectedSelectedPages.length > 0 &&
    disconnectedSelectedPages.every((page) =>
      dismissedPageIds.includes(page.pageId),
    );

  const shouldShow =
    isCalendar &&
    canAccessSocialLogin &&
    disconnectedSelectedPages.length > 0 &&
    !isDismissed;

  const bannerSignature = disconnectedSelectedPages
    .map((page) => `${page.pageId}:${page.pageName}:${page.postsAtRisk ?? 0}`)
    .join('|');

  useEffect(() => {
    if (!shouldShow) {
      return;
    }

    ownsBannerRef.current = true;

    showBanner(
      <CalendarDisconnectedProfilesBannerContent
        pages={disconnectedSelectedPages}
        onReconnect={handleReconnect}
      />,
      {
        variant: 'warning',
        // The slot centres its icon against the whole content block, which strands it
        // beside the chip row. The content renders its own icon inline with the message.
        icon: null,
        onClose: () => {
          setDismissedPageIds((current) =>
            writeDismissedPageIds([
              ...current,
              ...disconnectedSelectedPages.map((page) => page.pageId),
            ]),
          );
        },
      },
    );
    // Keyed on the rendered profiles rather than the array identity: showBanner writes
    // provider state, so depending on the array identity would re-enter this effect on
    // every provider render. Copy is translated inside the content component, so a
    // language change re-renders it without going through here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow, bannerSignature]);

  // The InfoBanner slot lives outside the router, so content set here would otherwise
  // follow the user into other sections. Only clear what this hook put there — the
  // payment banner shows once per session and must survive.
  //
  // clearBanner, not closeBanner: selecting a different profile makes this banner
  // irrelevant, it does not mean the user dismissed it. Running onClose here would record
  // the profiles as dismissed and hide the warning for the rest of the session.
  useEffect(() => {
    if (shouldShow || !ownsBannerRef.current) {
      return;
    }

    ownsBannerRef.current = false;
    clearBanner();
  }, [shouldShow, clearBanner]);

  useEffect(
    () => () => {
      if (ownsBannerRef.current) {
        ownsBannerRef.current = false;
        clearBanner();
      }
    },
    [clearBanner],
  );
};
